import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Mail, MessageSquare, Send, Sparkles, User, Repeat,
  Pause, Play, Square, CornerDownLeft, MapPin, Truck, CheckCircle2, XCircle,
} from "lucide-react";
import api, { apiError } from "../lib/api";

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail, agent: "Owen Reed · Agent 48 · Postmark" },
  { key: "sms", label: "SMS", icon: MessageSquare, agent: "Jasmine Bell · Agent 49 · Twilio" },
  { key: "mail", label: "Direct Mail", icon: Truck, agent: "Natalie Brooks · Agent 47 · PostGrid" },
];

function Field({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <input
        type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        data-testid={testid}
      />
    </label>
  );
}

function ThreadItem({ o }) {
  const inbound = o.direction === "inbound";
  const Icon = o.channel === "email" ? Mail : o.channel === "sms" ? MessageSquare : Truck;
  const failed = !!o.error;
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"}`} data-testid={`outreach-item-${o.id}`}>
      <div className={`max-w-[82%] rounded-xl border p-3 ${inbound ? "bg-muted/50 border-border" : failed ? "bg-destructive/5 border-destructive/30" : "bg-primary/5 border-primary/30"}`}>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
          <Icon className="h-3 w-3" />
          <span>{inbound ? "Owner reply" : `${o.channel} · ${o.step ? `touch ${o.step}` : "manual"}`}</span>
          {failed ? <XCircle className="h-3 w-3 text-destructive" /> : <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
          <span className="ml-auto">{new Date(o.created_at).toLocaleString()}</span>
        </div>
        {o.subject && <div className="text-sm font-semibold">{o.subject}</div>}
        <div className="text-sm whitespace-pre-wrap">{o.body}</div>
        {o.error && <div className="text-[11px] text-destructive mt-1">{o.error}</div>}
      </div>
    </div>
  );
}

export default function AcqOutreach() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [contact, setContact] = useState({});
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [consent, setConsent] = useState(false);
  const [live, setLive] = useState(false);
  const [thread, setThread] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/acq/leads/${leadId}`).then((r) => setLead(r.data)),
      api.get(`/acq/leads/${leadId}/contact`).then((r) => setContact(r.data.contact || {})),
      api.get(`/acq/leads/${leadId}/outreach`).then((r) => setThread(r.data.outreach || [])),
      api.get(`/acq/leads/${leadId}/sequences`).then((r) => setSequences(r.data.sequences || [])),
    ]).finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => {
    document.title = "ARQITEK — Outreach";
    load();
  }, [load]);

  const saveContact = async () => {
    try {
      const { data } = await api.patch(`/acq/leads/${leadId}/contact`, contact);
      setContact(data.contact);
      toast.success("Contact saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/acq/leads/${leadId}/outreach/draft`, { channel, touch: 1 });
      setSubject(data.subject || "");
      setBodyText(data.body || "");
      toast.success("Angela drafted your message");
    } catch (e) { toast.error(apiError(e)); } finally { setDrafting(false); }
  };

  const send = async () => {
    if (!bodyText.trim()) { toast.error("Write or draft a message first"); return; }
    setSending(true);
    try {
      let res;
      if (channel === "email") res = await api.post(`/acq/leads/${leadId}/outreach/email`, { subject, body: bodyText });
      else if (channel === "sms") res = await api.post(`/acq/leads/${leadId}/outreach/sms`, { body: bodyText, consent });
      else res = await api.post(`/acq/leads/${leadId}/outreach/mail`, { body: bodyText, live });
      if (res.data.ok) { toast.success("Sent"); setBodyText(""); setSubject(""); }
      else toast.error(res.data.error || "Send failed");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const enroll = async () => {
    try {
      const { data } = await api.post(`/acq/leads/${leadId}/sequence`, { channel, consent });
      if (data.ok) toast.success("4-touch sequence started");
      else toast.error(data.error || "Could not start sequence");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const seqAction = async (id, action) => {
    try {
      await api.post(`/acq/sequences/${id}/action`, { action });
      toast.success(`Sequence ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const logReply = async () => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/acq/leads/${leadId}/outreach/reply`, { channel, body: replyText });
      toast.success("Reply logged — cadence paused");
      setReplyText(""); setReplyOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const prop = lead?.subject_key?.address || lead?.subject_key?.apn || "Parcel";
  const activeCh = CHANNELS.find((c) => c.key === channel);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="acq-outreach-page">
      <Link to={`/ai-office/leads/${leadId}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="outreach-back">
        <ArrowLeft className="h-4 w-4" /> Back to lead
      </Link>

      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Acquisition Intelligence · Outreach</span>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" /> {prop}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Angela's outreach agents contact this owner — email, SMS or direct mail — and follow up on a 4-touch cadence that pauses the moment they reply.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: contact + compose */}
          <div className="lg:col-span-3 space-y-6">
            {/* Contact */}
            <div className="rounded-xl border border-border bg-card p-5" data-testid="outreach-contact">
              <div className="flex items-center gap-2 mb-4"><User className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Owner Contact</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} placeholder="Owner name" testid="contact-name" />
                <Field label="Email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} placeholder="owner@email.com" testid="contact-email" />
                <Field label="Phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} placeholder="+1 555 555 5555" testid="contact-phone" />
                <Field label="Mailing Address" value={contact.mailing_address} onChange={(v) => setContact({ ...contact, mailing_address: v })} placeholder="Street, City, ST, ZIP" testid="contact-mailing" />
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                Skip-trace is manual — enter what you've verified. Outreach can only run for a channel that has a real contact detail.
              </p>
              <button onClick={saveContact} className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="contact-save">Save Contact</button>
            </div>

            {/* Compose */}
            <div className="rounded-xl border border-border bg-card p-5" data-testid="outreach-compose">
              <div className="flex gap-1 border-b border-border mb-4">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button key={c.key} onClick={() => setChannel(c.key)}
                      className={`px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${channel === c.key ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                      data-testid={`channel-tab-${c.key}`}>
                      <Icon className="h-4 w-4" /> {c.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground mb-3">{activeCh?.agent}</div>

              {channel === "email" && (
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm mb-3 outline-none focus:border-primary" data-testid="compose-subject" />
              )}
              <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={channel === "sms" ? 4 : 8}
                placeholder={`Write the ${channel} message, or let Angela draft it…`}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary resize-y" data-testid="compose-body" />
              {channel === "sms" && <div className="text-[11px] text-muted-foreground mt-1 text-right">{bodyText.length} chars</div>}

              {channel === "sms" && (
                <label className="flex items-center gap-2 text-xs mt-3" data-testid="compose-consent">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  I have a documented consent basis to text this owner (10DLC/A2P compliant).
                </label>
              )}
              {channel === "mail" && (
                <label className="flex items-center gap-2 text-xs mt-3" data-testid="compose-live">
                  <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
                  Send LIVE (real physical mail + cost). Leave off for a PostGrid test piece.
                </label>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button onClick={draft} disabled={drafting} className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 text-primary px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="compose-draft">
                  {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Ask Angela to draft
                </button>
                <button onClick={send} disabled={sending} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="compose-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
                </button>
                <button onClick={enroll} className="inline-flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-sm font-medium" data-testid="compose-sequence">
                  <Repeat className="h-4 w-4" /> Start 4-touch sequence
                </button>
              </div>
            </div>
          </div>

          {/* Right: sequences + thread */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl border border-border bg-card p-5" data-testid="outreach-sequences">
              <div className="flex items-center gap-2 mb-3"><Repeat className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Follow-Up Sequences</span></div>
              {sequences.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="sequences-empty">No sequence running. Start one to auto-follow-up over 4 touches.</p>
              ) : sequences.map((s) => (
                <div key={s.id} className="rounded-lg border border-border p-3 mb-2" data-testid={`sequence-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize">{s.channel} · {s.status}</span>
                    <span className="text-xs text-muted-foreground">{(s.steps || []).filter((x) => x.sent).length}/{(s.steps || []).length} sent</span>
                  </div>
                  {s.paused_reason && <div className="text-[11px] text-amber-600 mt-1">{s.paused_reason}</div>}
                  <div className="flex gap-2 mt-2">
                    {s.status === "active" && <button onClick={() => seqAction(s.id, "pause")} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`seq-pause-${s.id}`}><Pause className="h-3 w-3" /> Pause</button>}
                    {s.status === "paused" && <button onClick={() => seqAction(s.id, "resume")} className="inline-flex items-center gap-1 text-xs text-primary" data-testid={`seq-resume-${s.id}`}><Play className="h-3 w-3" /> Resume</button>}
                    {s.status !== "stopped" && s.status !== "completed" && <button onClick={() => seqAction(s.id, "stop")} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive" data-testid={`seq-stop-${s.id}`}><Square className="h-3 w-3" /> Stop</button>}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-border bg-card p-5" data-testid="outreach-thread">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Conversation</span></div>
                <button onClick={() => setReplyOpen(!replyOpen)} className="inline-flex items-center gap-1 text-xs text-primary" data-testid="thread-log-reply">
                  <CornerDownLeft className="h-3 w-3" /> Log a reply
                </button>
              </div>
              {replyOpen && (
                <div className="mb-3">
                  <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} placeholder="Paste the owner's reply…"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" data-testid="reply-body" />
                  <button onClick={logReply} className="mt-2 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium" data-testid="reply-save">Log reply</button>
                </div>
              )}
              {thread.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="thread-empty">No messages yet.</p>
              ) : (
                <div className="space-y-3 max-h-[520px] overflow-y-auto">
                  {thread.map((o) => <ThreadItem key={o.id} o={o} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
